<template>
  <v-select
    v-model="templates.selected"
    :items="selectTemplateItems"
    :loading="templates.loading"
    :disabled="templates.loading"
    item-value="id"
    item-text="name"
    :filled="variant === 'filled'"
    :outlined="variant === 'outlined'"
    :rules="rules"
    :dense="dense"
    placeholder="Selecione um modelo"
    hide-details
  >
    <template #append-item>
      <div class="pa-2 pb-0 w-full d-flex">
        <v-btn class="new-btn w-full" depressed color="primary" @click="dialog.create = true">
          <v-icon left>mdi-plus</v-icon>
          Criar modelo
        </v-btn>
        <create-printing-template-dialog
          v-if="dialog.create"
          v-model="dialog.create"
          :type="type"
          locked
          @created="handleNewTemplate"
        />
      </div>
    </template>
    <template #item="{ item }">
      <v-radio :label="item.name" :off-icon="item.id === templates.selected ? '$radioOn' : '$radioOff'" />
    </template>
  </v-select>
</template>

<script>
import { AsyncMethod, Computed, Prop } from '@/_typedefs'
import { PrintingTemplate, PrintingTemplateType } from '@/plugins/api/endpoints/printingTemplate'
import { useV2Api } from '@/plugins/api/openapi'
import { useAuthStore } from '@/store/pinia/auth'
import { pick } from 'lodash'
import CreatePrintingTemplateDialog from './CreatePrintingTemplateDialog.vue'

const api = useV2Api()

export default {
  components: { CreatePrintingTemplateDialog },
  props: {
    /** @type {Prop<PrintingTemplate?>} */
    value: {
      type: Object,
      default: null,
    },
    /** @type {Prop<PrintingTemplateType?>} */
    type: {
      type: String,
      default: null,
    },
    /** @type {Prop<'filled' | 'outlined' | null>} */
    variant: {
      type: String,
      default: null,
    },
    dense: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      templates: {
        /** @type {Record<number, PrintingTemplate>} */
        items: {},
        loading: false,
        /** @type {number?} */
        selected: null,
      },
      dialog: {
        create: false,
      },
    }
  },
  computed: {
    /** @type {Computed<PrintingTemplate[]>} */
    selectTemplateItems() {
      const nameMap = {
        RECEIPT: 'Modelo padrão de receita',
        REPORT: 'Modelo padrão de relatório',
      }

      return [
        { id: null, name: nameMap[this.type] },
        ...Object.values(this.templates.items).map((item) => pick(item, ['id', 'name'])),
      ]
    },
  },
  methods: {
    /** @type {AsyncMethod} */
    async fetchTemplates() {
      try {
        this.templates.loading = true
        const data = await api.medicalDocumentTemplate.list({
          filter: {
            physicianId: {
              operation: 'equals',
              value: this.authStore.userId,
            },
            type: {
              operation: 'equals',
              value: this.type,
            },
          },
        })
        this.templates.items = Object.fromEntries(data.items.map((item) => [item.id, item]))
      } catch (error) {
        this.$root.error(error)
      } finally {
        this.templates.loading = false
      }
    },
    /** @type {Method<[template: PrintingTemplate]>} */
    handleNewTemplate(template) {
      this.$set(this.templates.items, template.id, template)
      this.templates.selected = template.id
    },
  },
  watch: {
    'templates.selected'(val) {
      this.$emit('input', this.templates.items[val] ?? null)
    },
  },
  async created() {
    await this.fetchTemplates()

    if (this.value && this.value.id in this.templates.items) {
      this.templates.selected = this.value.id
      return
    }

    const templateItemsIds = Object.keys(this.templates.items)
    if (templateItemsIds.length === 1) {
      this.templates.selected = this.templates.items[templateItemsIds[0]].id
    }
  },
  setup() {
    const authStore = useAuthStore()
    return {
      authStore,
    }
  },
}
</script>

<style scoped>
:deep(.v-radio .v-label) {
  color: #1d1b20;
}
</style>
